// Journey definitions.
//
// CACHE SCOPE (corrected 2026-09-20): the list endpoint returns the creator's
// journeys across every location they have, because a journey is authored once
// and switched on per site (see `enabledSalonIds`). Keying the cache per salon
// therefore wrote the SAME cross-site union under each site's key and read it
// back as if it belonged to the site you were standing in — switch location
// and the identical list reappeared under a new name, which is how a journey
// looked like it belonged somewhere it did not. The cache is now keyed to the
// account, and `enabledSalonIds` on each journey says where it actually runs.
//
// BACKED BY THE API since June 2026 (v2/journey/*) — localStorage is the
// cache; call hydrateJourneys() on mount (JourneysSection does) and
// mutations write through. The backend pins a snapshot of the definition
// onto every run at enrolment, so editing a journey never changes runs
// already in flight. The stored shape is the backend contract:
//
// {
//   id, name, status: 'ready' | 'paused',
//   trigger: { type: 'new-client'|'appointment-booked'|'appointment-completed'|'lapsed'|'birthday'|'no-show', lapsedWeeks? },
//   goalExit: true,            // leave the journey when the client books
//   steps: [
//     { id, type: 'wait', days },
//     { id, type: 'email', subject, blocks, emailHtml? },   // from the email designer;
//                                 // emailHtml only for themed (templates 2.0) designs
//     { id, type: 'sms', message },
//     { id, type: 'filter', condition: 'no-future-booking' | 'no-booking-since-start' },
//   ],
//   createdAt, updatedAt
// }

import { marketingBackend, isServerId } from '../../marketing-v2/services/marketingBackend';

// The list is per creator, not per site, so the key must be too. Falls back to
// the old per-salon key only when there is no account id to use.
const storageKey = (salonId) => {
  try {
    const userId = localStorage.getItem('userId');
    if (userId) return `beu-journeys-user-${userId}`;
  } catch (e) { /* private mode / blocked storage */ }
  return `beu-journeys-${salonId || 'default'}`;
};

// emailHtml (a themed email step's finished document, 6 to 26 KB each) is
// kept in memory and sent to the API, but never written to localStorage: a
// few designed journeys would eat the quota, and persist() swallows the error,
// so the cache would silently stop updating. The cached copy marks the step
// `hasEmailHtml` instead, and saveJourney puts the document back (from memory,
// or from the server) before posting, so a save can never drop it.
const memory = new Map();

const forCache = (list) => (list || []).map((j) => (j && Array.isArray(j.steps) && j.steps.some((st) => st && st.emailHtml)
  ? { ...j, steps: j.steps.map((st) => {
    if (!st || !st.emailHtml) return st;
    const { emailHtml, ...rest } = st;
    return { ...rest, hasEmailHtml: true };
  }) }
  : j));

const persist = (salonId, list) => {
  memory.set(storageKey(salonId), list);
  try { localStorage.setItem(storageKey(salonId), JSON.stringify(forCache(list))); } catch (e) { /* ignore */ }
  return list;
};

const findStepHtml = (list, journeyId, stepId) => {
  const j = (list || []).find((x) => x && x.id === journeyId);
  const st = j && (j.steps || []).find((x) => x && x.id === stepId);
  return (st && st.emailHtml) || null;
};

// The steps as the API should get them: every step marked hasEmailHtml gets
// its document back; the marker itself never leaves the browser.
export const stepsForApi = async (salonId, journeyId, steps, known) => {
  const list = steps || [];
  const held = known || memory.get(storageKey(salonId));
  // A duplicated step points at the step it was copied from.
  const source = (st) => (st.emailHtmlFrom ? [st.emailHtmlFrom.journeyId, st.emailHtmlFrom.stepId] : [journeyId, st.id]);
  const missing = (st) => st && st.hasEmailHtml && !st.emailHtml && !findStepHtml(held, ...source(st));
  let serverList = [];
  if (list.some(missing)) {
    try { serverList = ((await marketingBackend.journeyList()) || {}).journeys || []; } catch (e) { serverList = []; }
  }
  return list.map((st) => {
    if (!st || !('hasEmailHtml' in st || 'emailHtmlFrom' in st)) return st;
    const { hasEmailHtml, emailHtmlFrom, ...rest } = st;
    if (rest.emailHtml || !hasEmailHtml) return rest;
    const html = findStepHtml(held, ...source(st)) || findStepHtml(serverList, ...source(st));
    return html ? { ...rest, emailHtml: html } : rest;
  });
};

/** Pull the creator's journeys from the backend into the cache. */
export const hydrateJourneys = async (salonId) => {
  try {
    const server = await marketingBackend.journeyList();
    return persist(salonId, server.journeys || []);
  } catch (e) {
    console.error('journeys hydrate failed (cache kept):', e.message);
    return loadJourneys(salonId);
  }
};

// Test hook: forget the in-memory copy (a fresh page load).
export const _resetJourneyMemory = () => memory.clear();

export const loadJourneys = (salonId) => {
  const held = memory.get(storageKey(salonId));
  if (held) return held;
  try {
    const raw = localStorage.getItem(storageKey(salonId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

// A design saved from the email designer onto an email step. A themed design
// carries its finished `emailHtml` (what the server sends, exactly as for a
// themed campaign); a classic design has none, so any older one is dropped
// and the server renders the step from its blocks again.
export const applyStepDesign = (step, { blocks, subject, emailHtml } = {}) => {
  const { emailHtml: _stale, hasEmailHtml: _marker, emailHtmlFrom: _from, ...rest } = step || {};
  return {
    ...rest,
    blocks,
    ...(subject ? { subject } : {}),
    ...(emailHtml ? { emailHtml } : {}),
  };
};

export const saveJourney = (salonId, journey) => {
  const list = loadJourneys(salonId);
  const now = new Date().toISOString();
  const localId = journey.id || `jrn-${Date.now()}`;
  const stored = {
    ...journey,
    id: localId,
    createdAt: journey.createdAt || now,
    updatedAt: now,
  };
  persist(salonId, [stored, ...list.filter((j) => j.id !== stored.id)]);
  stepsForApi(salonId, journey.id, stored.steps, list).then((steps) => marketingBackend.journeySave({
    id: isServerId(localId) ? localId : undefined,
    name: stored.name,
    status: stored.status,
    trigger: stored.trigger,
    goalExit: stored.goalExit,
    steps,
  })).then((server) => {
    // Replace the local entry with the server one (ids converge on create).
    const fresh = loadJourneys(salonId).filter((j) => j.id !== localId && j.id !== server.journey.id);
    persist(salonId, [server.journey, ...fresh]);
  }).catch((e) => console.error('journey save failed:', e.message));
  return stored;
};

export const deleteJourney = (salonId, id) => {
  const next = loadJourneys(salonId).filter((j) => j.id !== id);
  persist(salonId, next);
  if (isServerId(id)) {
    marketingBackend.journeyDelete(id).catch((e) => console.error('journey delete failed:', e.message));
  }
  return next;
};

export const TRIGGERS = [
  { id: 'new-client', label: 'New client', description: 'Fires after a client completes their first appointment' },
  { id: 'appointment-booked', label: 'Appointment booked', description: 'Fires when a client books — your booking confirmation is still sent separately' },
  { id: 'appointment-completed', label: 'Appointment completed', description: 'Fires after any completed appointment' },
  { id: 'lapsed', label: 'Client goes quiet', description: 'Fires when a client has not visited for a while' },
  { id: 'birthday', label: 'Birthday', description: 'Fires on the morning of a client\'s birthday' },
  { id: 'no-show', label: 'Missed appointment', description: 'Fires when a client does not show up for an appointment' },
];

export const FILTER_CONDITIONS = [
  { id: 'no-future-booking', label: 'they have no upcoming appointment' },
  { id: 'no-booking-since-start', label: 'they have not booked since this journey began' },
];

export const triggerLabel = (trigger) => {
  const t = TRIGGERS.find((x) => x.id === trigger?.type);
  if (!t) return 'Trigger';
  if (trigger.type === 'lapsed') return `Client quiet for ${trigger.lapsedWeeks || 8} weeks`;
  return t.label;
};
